import { useState, useCallback, useMemo, lazy, Suspense, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@cieda/djayma-ui";
import {
  Menu,
  X,
  ChevronDown,
} from "lucide-react";

import { NAV_SECTIONS, type NavSection, type NavItem } from "./nav-sections";
import { ADMIN_THEME } from "@/lib/admin-theme";
import { DjaymaLogo } from "@/components/DjaymaLogo";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { useModulesQuery } from "@/hooks/admin/useModules";
import { ErrorBoundary } from "./shared/ErrorBoundary";
import { CommandPalette } from "./shared/CommandPalette";

// Lazy-load header slot stubs — they render nothing by default so we
// defer their chunk until the layout mounts
const NotificationBellSlot = lazy(() => import("./slots/NotificationBellSlot"));
const QuickMessageSlot = lazy(() => import("./slots/QuickMessageSlot"));

// ---------------------------------------------------------------------------
// Permission helper — delegates to AdminAuthContext for real permission checks.
// In dev-bypass mode (no Supabase configured) every item remains visible.
// ---------------------------------------------------------------------------

// Dev bypass: DISABLED — permission checks must always be enforced.
// Configure VITE_SUPABASE_URL in .env.local for local development.
const DEV_BYPASS = false;

function useCanAccess() {
  const { canAccess } = useAdminAuth();

  return (permission?: string): boolean => {
    // In dev bypass or when no permission key is specified, always show the item
    if (DEV_BYPASS || !permission) return true;
    // Delegate to the real permission context (checks department permissions)
    return canAccess(permission, "read");
  };
}

// ---------------------------------------------------------------------------
// Module-flag helper — hides nav items whose moduleKey is disabled.
// Items without a moduleKey are always visible (module gating is opt-in).
// While modules are loading, moduleKey-tagged items are hidden (fail-closed).
// ---------------------------------------------------------------------------

function useEnabledModuleKeys(): ReadonlySet<string> {
  const { data: modules } = useModulesQuery();

  return useMemo(() => {
    const set = new Set<string>();
    if (modules) {
      for (const m of modules) {
        if (m.isEnabled) {
          set.add(m.moduleKey);
        }
      }
    }
    return set;
  }, [modules]);
}

// ---------------------------------------------------------------------------
// Sidebar section (collapsible)
// ---------------------------------------------------------------------------

function SidebarSection({
  section,
  isOpen,
  onToggle,
  currentPath,
  canAccess,
  enabledModules,
  onNavigate,
}: {
  section: NavSection;
  isOpen: boolean;
  onToggle: () => void;
  currentPath: string;
  canAccess: (permission?: string) => boolean;
  enabledModules: ReadonlySet<string>;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation("admin");
  const Icon = section.icon;

  // Filter items by permission AND module flag
  const visibleItems = section.items.filter((item) => {
    if (!canAccess(item.permission)) return false;
    // If the nav item is tagged with a moduleKey, only show it when that
    // module is enabled. Items without a moduleKey are always visible.
    if (item.moduleKey && !enabledModules.has(item.moduleKey)) return false;
    return true;
  });
  if (visibleItems.length === 0) return null;

  // Check if any item in this section is active
  const hasActiveChild = visibleItems.some((item) => isPathActive(item.path, currentPath));

  return (
    <div>
      {/* Section header — clickable to expand / collapse */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors"
        style={{ color: hasActiveChild ? ADMIN_THEME.sidebar.textActive : ADMIN_THEME.sidebar.sectionLabel }}
      >
        <Icon
          className="h-4 w-4 shrink-0"
          style={{ color: hasActiveChild ? ADMIN_THEME.accent.gold : ADMIN_THEME.sidebar.sectionLabel }}
        />
        <span className="flex-1 text-left truncate">{t(section.labelKey)}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${
            isOpen ? "rotate-0" : "-rotate-90"
          }`}
          style={{ color: ADMIN_THEME.sidebar.sectionLabel }}
        />
      </button>

      {/* Collapsible item list */}
      {isOpen && (
        <div className="mt-0.5 ml-4 space-y-0.5 border-l pl-3" style={{ borderColor: ADMIN_THEME.sidebar.border }}>
          {visibleItems.map((item) => (
            <SidebarLink
              key={item.path}
              item={item}
              isActive={isPathActive(item.path, currentPath)}
              onClick={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar link (individual nav item)
// ---------------------------------------------------------------------------

function SidebarLink({
  item,
  isActive,
  onClick,
}: {
  item: NavItem;
  isActive: boolean;
  onClick?: () => void;
}) {
  const { t } = useTranslation("admin");

  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={`flex items-center rounded-md px-3 py-1.5 text-sm transition-colors ${
        isActive ? "font-medium" : ""
      }`}
      style={{
        color: isActive ? ADMIN_THEME.sidebar.textActive : ADMIN_THEME.sidebar.text,
        background: isActive ? ADMIN_THEME.sidebar.activeBg : undefined,
      }}
      onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = ADMIN_THEME.sidebar.hoverBg; }}
      onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = ""; }}
    >
      <span className="truncate">{t(item.labelKey)}</span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Path matching helper
// ---------------------------------------------------------------------------

function isPathActive(itemPath: string, currentPath: string): boolean {
  if (itemPath === "/admin") return currentPath === "/admin";
  return currentPath.startsWith(itemPath);
}

// ---------------------------------------------------------------------------
// Determine which sections should be open by default based on current route
// ---------------------------------------------------------------------------

function getInitialOpenSections(pathname: string): Record<string, boolean> {
  const open: Record<string, boolean> = {};
  for (const section of NAV_SECTIONS) {
    const hasActive = section.items.some((item) => isPathActive(item.path, pathname));
    if (hasActive) {
      open[section.key] = true;
    }
  }
  // Always open the first section (Commerce / Dashboard) if nothing else matches
  if (Object.keys(open).length === 0) {
    open[NAV_SECTIONS[0].key] = true;
  }
  return open;
}

// ---------------------------------------------------------------------------
// AdminLayout
// ---------------------------------------------------------------------------

interface AdminLayoutProps {
  children: ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const { t } = useTranslation("admin");
  const { user: adminUser } = useAdminAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() =>
    getInitialOpenSections(location.pathname),
  );

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const canAccess = useCanAccess();
  const enabledModules = useEnabledModuleKeys();

  const toggleSection = useCallback((key: string) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Shared sidebar content — rendered in both desktop sidebar and mobile drawer
  const sidebarContent = (
    <>
      {/* Sidebar header */}
      <div className="flex h-16 items-center justify-between px-4" style={{ borderBottom: `1px solid ${ADMIN_THEME.sidebar.border}` }}>
        <Link to="/admin" className="flex items-center gap-2">
          <DjaymaLogo height={32} variant="on-terracotta" />
        </Link>
        <button
          onClick={closeSidebar}
          className="rounded-md p-1 md:hidden"
          style={{ color: ADMIN_THEME.sidebar.text }}
          aria-label="Fermer le menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Navigation sections */}
      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {NAV_SECTIONS.map((section) => (
          <SidebarSection
            key={section.key}
            section={section}
            isOpen={!!openSections[section.key]}
            onToggle={() => toggleSection(section.key)}
            currentPath={location.pathname}
            canAccess={canAccess}
            enabledModules={enabledModules}
            onNavigate={closeSidebar}
          />
        ))}
      </nav>

      {/* Sidebar footer */}
      <div className="p-4" style={{ borderTop: `1px solid ${ADMIN_THEME.sidebar.border}` }}>
        <div className="flex items-center gap-3">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-medium"
            style={{ background: ADMIN_THEME.accent.gold, color: ADMIN_THEME.accent.green }}
          >
            {adminUser?.fullName?.charAt(0)?.toUpperCase() ?? "A"}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium" style={{ color: ADMIN_THEME.sidebar.textActive }}>
              {adminUser?.fullName ?? "Admin"}
            </p>
            <p className="truncate text-xs" style={{ color: ADMIN_THEME.sidebar.text }}>
              {adminUser?.departmentName ?? t("header.admin_label")}
            </p>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen" style={{ background: ADMIN_THEME.content.bg }}>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 md:hidden"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col transition-transform duration-200 md:static md:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ background: `linear-gradient(180deg, ${ADMIN_THEME.sidebar.bg} 0%, ${ADMIN_THEME.sidebar.bgDark} 100%)` }}
      >
        {sidebarContent}
      </aside>

      {/* Main content area */}
      <div className="flex flex-1 flex-col">
        {/* Header (visible on all breakpoints) */}
        <header className="flex h-16 items-center justify-between border-b px-4" style={{ borderColor: ADMIN_THEME.content.border, background: ADMIN_THEME.content.bg }}>
          <div className="flex items-center gap-4">
            {/* Mobile hamburger */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(true)}
              aria-label="Ouvrir le menu"
              className="md:hidden"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <span className="font-display text-lg font-semibold md:hidden" style={{ color: ADMIN_THEME.accent.green }}>
              {t("header.title")}
            </span>
          </div>

          {/* Header right — notification bell + quick message slots */}
          <div className="flex items-center gap-2">
            <Suspense fallback={null}>
              <NotificationBellSlot />
            </Suspense>
            <Suspense fallback={null}>
              <QuickMessageSlot />
            </Suspense>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8">
          <ErrorBoundary>{children}</ErrorBoundary>
        </main>
      </div>

      {/* Command palette (Cmd+K / Ctrl+K) */}
      <CommandPalette />
    </div>
  );
}
